import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";

import { UserActivateModalStyles } from "./styled";
import { Modal } from "react-bootstrap";
import Icon from "components/Icon/Icon";
import EnvelopeSimpleOutlineRegularIcon from "assets/SVGComponents/EnvelopeSimpleOutlineRegularIcon";
import { X } from "@phosphor-icons/react";
import Button from "components/Buttons";
import Input from "components/Input/index";
import { useUsersSlice } from "pages/Users/slice/index";
import { useValidationSlice } from "store/validation/slice/index";
import { triggerEmailValidation } from "store/validation/slice/selector";
import LoadingSpinner from "components/PageLoader/Spinner";
import { selectVerifyEmailAndActivateInfo } from "pages/Users/slice/selectors";

const UserActivateModal = ({
  show,
  onHide,
  userInfo,
  loading,
  updateType = null,
  currentStatus
}) => {
  const dispatch = useDispatch();
  const { actions } = useUsersSlice();
  const { actions: emailValidationActions } = useValidationSlice();
  const { success, error } = useSelector(triggerEmailValidation);
  const {
    loading: emailValidationLoading,
    error: activateError,
    success: activateSuccess
  } = useSelector(selectVerifyEmailAndActivateInfo);

  const [activateEmail, setActivateEmail] = useState(userInfo?.email || "");
  const secret_code = process.env.REACT_APP_SECRET_CODE;
  const isValidatingRef = useRef(false);

  useEffect(() => {
    if (show) {
      setActivateEmail(userInfo?.email || "");
    }
  }, [show]);

  useEffect(() => {
    if (!isValidatingRef.current) return;

    if (activateSuccess) {
      isValidatingRef.current = false;
      onHide();
    }

    if (activateError) {
      isValidatingRef.current = false;
      // modal stays open, error shown in helperText
    }
  }, [activateSuccess, activateError]);

  const handleApiCall = () => {
    if (userInfo?.status === "not_filled") {
      isValidatingRef.current = true;
      dispatch(
        actions.verifyEmailAndActivate({
          email: activateEmail,
          user_id: userInfo.id,
          secret_code,
          updateType
        })
      );
      return;
    }
    if (currentStatus === "activated") {
      dispatch(
        actions.userActivate({
          user_id: userInfo.id,
          secret_code,
          updateType,
          userStatus: userInfo.status
        })
      );
      onHide();
    }

    if (currentStatus === "deactivated") {
      dispatch(
        actions.userDeactivate({
          user_id: userInfo.id,
          secret_code,
          updateType,
          userStatus: userInfo.status
        })
      );
      onHide();
    }
  };

  return (
    <UserActivateModalStyles
      show={show}
      className="centered-modal"
      backdrop={loading ? "static" : true}
    >
      <Modal.Header>
        <Modal.Title>
          {userInfo?.status === "activated" || currentStatus === "deactivated"
            ? "Deactivate"
            : "Activate"}{" "}
          this Account?
        </Modal.Title>
        <div onClick={onHide} className="close-icon-wrapper">
          <Icon component={X} size={24} />
        </div>
      </Modal.Header>
      <Modal.Body>
        {userInfo?.status === "activated" || currentStatus === "deactivated" ? (
          <p>
            Deactivating this account will block access to the HashFame app and
            pause all account activity until it’s reactivated.
          </p>
        ) : (
          <>
            <p>
              Adding an email address will activate this account. Once
              activated, the user will have full access to the HashFame app and
              related features.
            </p>
            {userInfo?.status === "not_filled" && (
              <Input
                id="email"
                label="Email"
                placeholder="john.doe@mail.com"
                size="M"
                helperText={activateError}
                leftIcon={<EnvelopeSimpleOutlineRegularIcon />}
                value={activateEmail}
                onChange={(e) => setActivateEmail(e.target.value)}
              />
            )}
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Cancel
        </Button>
        {userInfo?.status === "activated" || currentStatus === "deactivated" ? (
          <Button
            variant="primary"
            colorScheme="error"
            size="M"
            onClick={handleApiCall}
          >
            Deactivate Account
          </Button>
        ) : (
          <Button
            size="M"
            variant="primary"
            onClick={handleApiCall}
            disabled={
              (userInfo?.status === "not_filled" && !activateEmail) ||
              emailValidationLoading
            }
          >
            {emailValidationLoading ? (
              <LoadingSpinner size={16} color="#fff" />
            ) : userInfo?.status === "not_filled" ? (
              "Add Email and Activate"
            ) : (
              "Activate Account"
            )}
          </Button>
        )}
      </Modal.Footer>
    </UserActivateModalStyles>
  );
};

export default UserActivateModal;
